import { Button, Drawer, Form, Input, Select, Spin, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import AssetLocationService, {
  IFALocation,
  IFALocationList,
} from "../../../Services/AssetLocationService";
import { useClient } from "../../../Context/ClientContext";
import { LoadingOutlined } from "@ant-design/icons";
import { notificationContext } from "../../../Common/Routing/PageRoute";
import OrganisationsService, {
  ISubUnitOptionsList,
} from "../../../Services/Organisations/OrganisationsService";
import { LedgerStatus } from "../../../Services/LedgerService";
import type { DrawerProps } from "antd";
import { useHotkey } from "../../../Context/HotKeyContext";
import EntryLevelKeys from "../../../Common/EntryLevelKeys";
interface Modelprops {
  onCancel: React.Dispatch<
    React.SetStateAction<{
      type: string;
      open: boolean;
    }>
  >;
  isModelOpen: boolean;
  id?: string;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
  setAddAssLocation?: React.Dispatch<React.SetStateAction<IFALocation>>;
  placement?: DrawerProps["placement"];
}
const AddAssetLocationModal = ({
  isModelOpen,
  onCancel,
  id,
  setRefresh,
  setAddAssLocation,
  placement,
}: Modelprops) => {
  const { showKeys } = useHotkey();
  const [form] = Form.useForm<IFALocation>();
  const { currentClient } = useClient();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [btnLoading, setBtnLoading] = React.useState<boolean>(false);
  const [faLocationNameAvailable, setFALocationNameAvailable] =
    React.useState<boolean>(false);
  const [subUnitsList, setSubUnitsList] = useState<ISubUnitOptionsList[]>([]);
  const [faLocationsList, setFALocationsList] = useState<IFALocationList[]>([]);
  const { openNotification } = React.useContext(notificationContext);

  // To get all sub units
  const getAllSubUnits = async () => {
    await OrganisationsService.getSubUnitOptionsList(currentClient?.id!)
      .then((res) => {
        if (res) {
          setSubUnitsList(res);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const getAssetLocationsList = async () => {
    await AssetLocationService.getLocationList(currentClient?.id!, 0, 0, "")
      .then((res) => {
        if (res.result?.length > 0) {
          setFALocationsList(res.result);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Post asset location data
  const addLocationData = () => {
    form.validateFields().then(async (val) => {
      setLoading(true);
      let res = await AssetLocationService?.postLocationData(
        currentClient?.id,
        val,
        id
      );
      if (res?.status) {
        setLoading(false);
        setRefresh && setRefresh((x: boolean) => !x);
        setAddAssLocation &&
          setAddAssLocation({
            locationName: res.result.locationName,
            id: res.result.id,
            subUnitId: res.result?.subUnitId,
            subUnitName: res.result?.subUnitName,
          });
        openNotification(
          "success",
          `Location ${id ? "updated successfully." : "added successfully."}`
        );
        onCancel({ open: false, type: "" });
      } else {
        setLoading(false);
        openNotification("error", res?.message);
      }
    });
  };

  // To get all necessary data
  useEffect(() => {
    setLoading(true);
    Promise.all([getAllSubUnits(), getAssetLocationsList()])
      .then(() => {
        setLoading(false);
      })
      .catch((e: any) => {
        console.log("Error", e?.message);
        setLoading(false);
      });
  }, [currentClient?.id]);

  // To get asset location data by Id
  const getDatabyId = async (id: string) => {
    setLoading(true);
    let res = await AssetLocationService?.getLocationDatabyId(
      currentClient?.id,
      id
    );
    if (res.status) {
      form?.setFieldsValue(res?.result);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    if (id) {
      getDatabyId(id);
    }
  }, [id]);

  // To check duplicate value
  const checkDuplidateFALocation = async (e: any) => {
    let ledDetail = faLocationsList
      ?.filter((led) => led.id !== id)
      .filter(
        (led) =>
          led.locationName.toLowerCase() ===
          e?.target?.value.trim().toLowerCase()
      );
    setFALocationNameAvailable(ledDetail?.length! > 0 ? true : false);
  };

  EntryLevelKeys({
    save: () => addLocationData(),
    cancel: () => onCancel({ open: false, type: "" }),
  });

  return (
    <div>
      <Drawer
        open={isModelOpen}
        placement={placement}
        width={"30vw"}
        onClose={() => onCancel({ open: false, type: "" })}
        title={`${id ? "Update" : "Add"} Location`}
        maskClosable={false}
        extra={
          <div style={{ marginTop: "0px" }}>
            <Tooltip
              title={<p>ALT+C</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                onClick={() => onCancel({ open: false, type: "" })}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </Button>
            </Tooltip>
            <Tooltip
              title={<p>ALT+S</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                loading={loading}
                type="primary"
                onClick={addLocationData}
              >
                {id ? "Edit" : "Save"}
              </Button>
            </Tooltip>
          </div>
        }
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 20 }} />}
          spinning={loading}
        >
          <Form form={form} layout="vertical" style={{ marginTop: "20px" }}>
            <Form.Item
              name="locationName"
              label="Location Name"
              rules={[
                {
                  required: true,
                  pattern:
                    /^[A-Za-z][A-Za-z0-9_/()-]+(?:\s[A-Za-z0-9_/()-]+)*$/,
                  message:
                    "Asset location name is required. Only alphanumeric characters with space, (, ), _, / and - are allowed",
                },
                {
                  min: 3,
                  max: 30,
                  message:
                    "Location name's length must be minimum 3 and maximum 30",
                },
                {
                  validator: () => {
                    if (faLocationNameAvailable) {
                      return Promise.reject(
                        "Asset location's name already exists."
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <Input maxLength={30} onChange={checkDuplidateFALocation} />
            </Form.Item>
            <Form.Item name="subUnitId" label="Sub Unit">
              <Select options={[{}]} />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};

export default AddAssetLocationModal;
